import { Command } from 'ckeditor5';
import { ConditionState } from './State';
import { buildMarkerNameFor, isConditionMarker } from './markerName';
/**
 * Returns true when ranges A and B interlace (partial overlap without full containment).
 * Concretely: a1 < b1 < a2 < b2  OR  b1 < a1 < b2 < a2.
 * Proper nesting and disjoint ranges both return false.
 */
function interlaces(a, b) {
    const { start: a1, end: a2 } = a;
    const { start: b1, end: b2 } = b;
    return ((a1.isBefore(b1) && b1.isBefore(a2) && a2.isBefore(b2)) || (b1.isBefore(a1) && a1.isBefore(b2) && b2.isBefore(a2)));
}
export class InsertConditionCommand extends Command {
    static get pluginName() {
        return 'insertCondition';
    }
    constructor(editor) {
        super(editor);
        this.state = this.editor.plugins.get(ConditionState);
    }
    refresh() {
        const { model } = this.editor;
        const { selection } = model.document;
        if (selection.isCollapsed) {
            this.isEnabled = false;
            return;
        }
        const range = selection.getFirstRange();
        if (!range) {
            this.isEnabled = false;
            return;
        }
        this.isEnabled = !this.wouldInterlace(range);
    }
    /**
     * Wraps the current selection in a condition and returns the new marker name, or `null` when the
     * selection cannot carry one.
     *
     * Defaults to an **unconfigured** condition — `assume` with an empty ref — so insertion never
     * invents an identifier that resolves to nothing. The caller is expected to open its editor and
     * let the author pick a question.
     */
    execute(options = {}) {
        const { predicate = 'assume', ref = '', choiceKey } = options;
        const { model } = this.editor;
        let markerName = null;
        model.change((writer) => {
            const { selection } = model.document;
            const firstRange = selection.getFirstRange();
            if (!firstRange || firstRange.isCollapsed) {
                return;
            }
            if (this.wouldInterlace(firstRange)) {
                return;
            }
            markerName = buildMarkerNameFor({ predicate, ref, choiceKey });
            writer.addMarker(markerName, {
                usingOperation: true,
                affectsData: true,
                range: firstRange,
            });
            this.state.addCondition(markerName);
            writer.setSelection(firstRange.end);
        });
        return markerName;
    }
    /** @deprecated Use `execute()` — kept so existing callers keep working. */
    addCondition(options) {
        return this.execute(options);
    }
    wouldInterlace(range) {
        for (const marker of this.editor.model.markers) {
            if (!isConditionMarker(marker.name)) {
                // eslint-disable-next-line no-continue
                continue;
            }
            if (interlaces(range, marker.getRange())) {
                return true;
            }
        }
        return false;
    }
}
